/**
 * 게시판 관련 Write 작업 목차
 * 
 * - createPost() - 게시글 작성
 * - updatePost() - 게시글 수정
 * - deletePost() - 게시글 삭제
 * - togglePostLike() - 게시글 좋아요 토글
 */

import apiClient from "@/utils/api";
import { addActivityPoints } from "@/utils/write/user";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";

/**
 * 게시글 작성
 * @param {Object} postData - 게시글 데이터
 * @returns {Promise<string>} 생성된 게시글 ID
 */
export async function createPost(postData) {
  try {
    const response = await apiClient.post("/api/board", postData);
    
    // 게시글 작성 점수 추가
    if (postData.writerEmail) {
      await addActivityPoints(postData.writerEmail, "POST_WRITE");
    }

    const postId = response?.data?.id || response?.data?.data?.id;
    if (!postId) {
      throw new Error("게시글 ID를 응답에서 찾을 수 없습니다.");
    }

    // 게시글 작성 활동 기록
    trackActivity(ACTIVITY_TYPES.POST_CREATE, {
      target: "board",
      targetId: postId,
      metadata: { title: postData.title, category: postData.category },
    });

    return postId;
  } catch (error) {
    console.error("게시글 작성 오류:", error);
    throw error;
  }
}

/**
 * 게시글 수정
 * @param {string} postId - 게시글 ID
 * @param {Object} updateData - 수정할 데이터
 * @returns {Promise<void>}
 */
export async function updatePost(postId, updateData) {
  try {
    await apiClient.put(`/api/board/${postId}`, updateData);

    // 게시글 수정 활동 기록
    trackActivity(ACTIVITY_TYPES.POST_UPDATE, {
      target: "board",
      targetId: postId,
      metadata: { title: updateData.title },
    });
  } catch (error) {
    console.error("게시글 수정 오류:", error);
    throw error;
  }
}

/**
 * 게시글 삭제
 * @param {string} postId - 게시글 ID
 * @returns {Promise<void>}
 */
export async function deletePost(postId) {
  try {
    await apiClient.delete(`/api/board/${postId}`);
  } catch (error) {
    console.error("게시글 삭제 오류:", error);
    throw error;
  }
}

/**
 * 게시글 좋아요 토글
 * @param {string} postId - 게시글 ID
 * @param {string} userEmail - 사용자 이메일
 * @param {boolean} isCurrentlyLiked - 현재 좋아요 상태
 * @returns {Promise<boolean>} 새로운 좋아요 상태
 */
export async function togglePostLike(postId, userEmail, isCurrentlyLiked) {
  try {
    const response = await apiClient.post(`/api/board/${postId}/like`, {
      userEmail,
      isLiked: isCurrentlyLiked,
    });

    const newLikedState = response.data.data.isLiked;

    // 점수 처리
    if (newLikedState) {
      await addActivityPoints(userEmail, "POST_LIKE");
    } else {
      await addActivityPoints(userEmail, "POST_LIKE_CANCEL");
    }

    // 게시글 추천 활동 기록
    trackActivity(newLikedState ? ACTIVITY_TYPES.POST_LIKE : ACTIVITY_TYPES.POST_UNLIKE, {
      target: "board",
      targetId: postId,
    });

    return newLikedState;
  } catch (error) {
    console.error("게시글 좋아요 오류:", error);
    throw error;
  }
}
